// Firestore shell for the private, context-aware rolodex.
//
// Private contact details live below the owner at
// users/{ownerUid}/rolodex/{entryId}. The only globally readable relationship
// data is a minimal network_edges document containing two registered-user IDs.
// Every mutation writes the private entry, uniqueness claim, and Discovery Edge
// in one retryable transaction.

import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import { db, isDemoMode } from "../firebase";
import { loadPublishedProfile } from "./profileStore";
import {
  ROLODEX_CONTEXTS,
  duplicateRecipientError,
  prepareRolodexEntry,
  rolodexPublicationPlan,
  SHARED_FIELDS,
} from "./rolodexMutation";

export {
  ROLODEX_CONTEXTS,
  defaultDiscoveryFor,
  normalizeRolodexFacet,
  shouldPublishDiscoveryEdge,
} from "./rolodexMutation";

function assertOwner(ownerUid) {
  if (!ownerUid || typeof ownerUid !== "string") {
    throw new Error("A rolodex owner UID is required.");
  }
}

function assertContext(context) {
  if (!ROLODEX_CONTEXTS.includes(context)) {
    throw new Error(`Unknown rolodex context: ${context}`);
  }
}

function hasOwn(value, key) {
  return Object.prototype.hasOwnProperty.call(value || {}, key);
}

// Length-prefixing avoids collisions between unusual custom Auth UIDs. Encoding
// keeps '/' out of the Firestore document ID.
export function networkEdgeId(sourceUid, targetUid) {
  const source = encodeURIComponent(sourceUid);
  const target = encodeURIComponent(targetUid);
  return `${source.length}_${source}_${target}`;
}

function rolodexCollection(ownerUid) {
  return collection(db, "users", ownerUid, "rolodex");
}

function rolodexDocument(ownerUid, entryId) {
  return doc(db, "users", ownerUid, "rolodex", entryId);
}

function edgeDocument(sourceUid, targetUid) {
  return doc(db, "network_edges", networkEdgeId(sourceUid, targetUid));
}

function recipientIndexDocument(ownerUid, targetUid) {
  if (targetUid.includes("/")) {
    const error = new Error("This registered user ID cannot be indexed.");
    error.code = "rolodex/invalid-recipient";
    throw error;
  }
  return doc(
    db,
    "users",
    ownerUid,
    "rolodex_recipient_index",
    targetUid
  );
}

/** Fetch a registered user's public profile, or null if it is unavailable. */
export async function loadRolodexProfile(uid) {
  if (!uid || isDemoMode) return null;
  try {
    return await loadPublishedProfile(uid);
  } catch (_) {
    return null;
  }
}

async function enrichEntry(entry) {
  return {
    ...entry,
    otherProfile: entry.recipient_uid
      ? await loadRolodexProfile(entry.recipient_uid)
      : null,
  };
}

function timestampMillis(value) {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  const millis = new Date(value).getTime();
  return Number.isNaN(millis) ? 0 : millis;
}

async function enrichAndSort(docs) {
  const entries = await Promise.all(
    docs.map(async (snapshot) =>
      enrichEntry({ id: snapshot.id, ...snapshot.data() })
    )
  );
  return entries.sort(
    (a, b) => timestampMillis(b.created_at) - timestampMillis(a.created_at)
  );
}

/** Subscribe to all of an owner's private rolodex entries. */
export function subscribeRolodex(ownerUid, callback, onError = () => {}) {
  if (isDemoMode || !ownerUid) {
    callback([]);
    return () => {};
  }

  let revision = 0;
  const unsubscribe = onSnapshot(
    rolodexCollection(ownerUid),
    (snapshot) => {
      const currentRevision = ++revision;
      enrichAndSort(snapshot.docs)
        .then((entries) => {
          if (currentRevision === revision) callback(entries);
        })
        .catch(onError);
    },
    onError
  );
  return () => {
    revision += 1;
    unsubscribe();
  };
}

/** Subscribe to one private rolodex entry, enriched with its user profile. */
export function subscribeRolodexEntry(
  ownerUid,
  entryId,
  callback,
  onError = () => {}
) {
  if (isDemoMode || !ownerUid || !entryId) {
    callback(null);
    return () => {};
  }

  let revision = 0;
  const unsubscribe = onSnapshot(
    rolodexDocument(ownerUid, entryId),
    (snapshot) => {
      const currentRevision = ++revision;
      if (!snapshot.exists()) {
        callback(null);
        return;
      }
      enrichEntry({ id: snapshot.id, ...snapshot.data() })
        .then((entry) => {
          if (currentRevision === revision) callback(entry);
        })
        .catch(onError);
    },
    onError
  );
  return () => {
    revision += 1;
    unsubscribe();
  };
}

/** Read every minimal discovery edge (authenticated users only). */
export async function loadNetworkEdges() {
  if (isDemoMode) return [];
  const snapshot = await getDocs(collection(db, "network_edges"));
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

/** Live subscription to the authenticated-readable discovery graph. */
export function subscribeNetworkEdges(callback, onError = () => {}) {
  if (isDemoMode) {
    callback([]);
    return () => {};
  }
  return onSnapshot(
    collection(db, "network_edges"),
    (snapshot) =>
      callback(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))),
    onError
  );
}

function entryIdOf(entryOrId) {
  return typeof entryOrId === "string" ? entryOrId : entryOrId?.id;
}

async function commitEntryIntent(
  ownerUid,
  entryId,
  buildNext,
  { requireExisting = false } = {}
) {
  const ref = rolodexDocument(ownerUid, entryId);

  return runTransaction(db, async (transaction) => {
    const previousSnapshot = await transaction.get(ref);
    const previous = previousSnapshot.exists()
      ? { id: previousSnapshot.id, ...previousSnapshot.data() }
      : null;

    if (requireExisting && !previous) {
      throw new Error("Rolodex entry not found.");
    }

    const prepared = buildNext(previous);
    if (!previous && prepared && Object.keys(prepared.facets || {}).length === 0) {
      throw new Error("A new rolodex entry needs at least one context.");
    }
    if (prepared?.recipient_uid === ownerUid) {
      throw new Error("You cannot add yourself to your own rolodex.");
    }

    const next = prepared && Object.keys(prepared.facets || {}).length > 0
      ? {
          ...prepared,
          ...(prepared.recipient_uid
            ? { discovery_edge_id: networkEdgeId(ownerUid, prepared.recipient_uid) }
            : {}),
          created_at: previous?.created_at || serverTimestamp(),
          updated_at: serverTimestamp(),
        }
      : null;
    if (next && !next.recipient_uid) delete next.discovery_edge_id;
    const plan = rolodexPublicationPlan(entryId, previous, next);

    // All reads happen before writes. Reading the unique-recipient index makes
    // concurrent creates contend on one document and lets Firestore retry the
    // losing transaction against the committed claim.
    const indexTargets = [...new Set(
      [plan.oldTarget, plan.newTarget].filter(Boolean)
    )];
    const indexSnapshots = new Map();
    await Promise.all(
      indexTargets.map(async (targetUid) => {
        const indexRef = recipientIndexDocument(ownerUid, targetUid);
        indexSnapshots.set(targetUid, {
          ref: indexRef,
          snapshot: await transaction.get(indexRef),
        });
      })
    );

    if (plan.claimNewIndex) {
      const claim = indexSnapshots.get(plan.newTarget)?.snapshot;
      const claimedEntryId = claim?.exists() ? claim.data()?.entry_id : null;
      if (claimedEntryId && claimedEntryId !== entryId) {
        throw duplicateRecipientError(claimedEntryId);
      }
    }

    if (next) transaction.set(ref, next);
    else if (previous) transaction.delete(ref);

    if (plan.removeOldIndex) {
      const oldIndex = indexSnapshots.get(plan.oldTarget);
      if (
        oldIndex?.snapshot.exists() &&
        oldIndex.snapshot.data()?.entry_id === entryId
      ) {
        transaction.delete(oldIndex.ref);
      }
    }
    if (plan.claimNewIndex) {
      transaction.set(indexSnapshots.get(plan.newTarget).ref, {
        entry_id: entryId,
        recipient_uid: plan.newTarget,
      });
    }

    plan.removeEdgeTargets.forEach((targetUid) => {
      transaction.delete(edgeDocument(ownerUid, targetUid));
    });
    if (plan.publishNewEdge) {
      transaction.set(edgeDocument(ownerUid, plan.newTarget), {
        source_uid: ownerUid,
        target_uid: plan.newTarget,
      });
    }

    return entryId;
  });
}

/**
 * Create or replace the supplied facets/shared identity for one entry.
 * Omitted facets are preserved; setting a facet to null removes it. Removing
 * the last facet deletes the entry and its discovery edge.
 */
export async function saveRolodexEntry(ownerUid, entry) {
  assertOwner(ownerUid);
  if (isDemoMode) return null;

  const requestedId = entryIdOf(entry);
  const ref = requestedId
    ? rolodexDocument(ownerUid, requestedId)
    : doc(rolodexCollection(ownerUid));
  return commitEntryIntent(ownerUid, ref.id, (previous) =>
    prepareRolodexEntry(entry || {}, previous)
  );
}

/** Alias that makes create intent explicit while retaining one save contract. */
export const createRolodexEntry = saveRolodexEntry;

/**
 * Add, update, or remove one facet. `entry` may be the full UI entry or its ID;
 * shared identity fields on a full entry are saved with the facet.
 */
export async function updateRolodexFacet(ownerUid, entry, context, facet) {
  assertOwner(ownerUid);
  assertContext(context);
  if (isDemoMode) return null;

  const entryId = entryIdOf(entry);
  if (!entryId) throw new Error("A rolodex entry ID is required.");
  const patch = { facets: { [context]: facet } };
  if (entry && typeof entry === "object") {
    SHARED_FIELDS.forEach((field) => {
      if (hasOwn(entry, field)) patch[field] = entry[field];
    });
  }

  return commitEntryIntent(
    ownerUid,
    entryId,
    (previous) => prepareRolodexEntry(patch, previous),
    { requireExisting: true }
  );
}

export function removeRolodexFacet(ownerUid, entry, context) {
  return updateRolodexFacet(ownerUid, entry, context, null);
}

/** Remove an entire private entry, uniqueness claim, and edge atomically. */
export async function removeRolodexEntry(ownerUid, entry) {
  assertOwner(ownerUid);
  if (isDemoMode) return;

  const entryId = entryIdOf(entry);
  if (!entryId) throw new Error("A rolodex entry ID is required.");
  await commitEntryIntent(ownerUid, entryId, () => null);
}
